import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, BarChart, Bar, Legend,
} from "recharts";
import { BarChart3 } from "lucide-react";
import PlatformShell from "../components/PlatformShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import api from "../services/api";

const fmtMoney = (v, sign = false) => {
  const n = Number(v || 0);
  return (n < 0 ? "-$" : (sign ? "+$" : "$")) + Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

// Semicircle SVG gauge — no chart-lib dependency, full control.
function Gauge({ value, max, label, sublabel, caption, color }) {
  const frac = Math.max(0, Math.min(1, max ? value / max : 0));
  const r = 80;
  const angle = Math.PI * (1 - frac);
  const x = 100 + r * Math.cos(angle);
  const y = 100 - r * Math.sin(angle);
  const arc = (sx, sy, ex, ey) => `M ${sx} ${sy} A ${r} ${r} 0 0 1 ${ex} ${ey}`;
  return (
    <div className="flex flex-col items-center">
      <div className="text-sm font-semibold text-zinc-300 mb-1">{label}</div>
      <svg viewBox="0 0 200 120" className="w-full max-w-[220px]">
        <path d={arc(20, 100, 180, 100)} fill="none" stroke="#3f3f46" strokeWidth="14" strokeLinecap="round" />
        <path d={arc(20, 100, x, y)} fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" />
        <text x="100" y="92" textAnchor="middle" className="fill-white" style={{ fontSize: 26, fontWeight: 700 }}>
          {sublabel}
        </text>
      </svg>
      {caption && <div className="text-xs text-zinc-500 -mt-1">{caption}</div>}
    </div>
  );
}

function StatRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800/60 last:border-0">
      <span className="text-zinc-400 text-sm">{label}</span>
      <span className={`font-mono text-sm ${valueClass}`}>{value}</span>
    </div>
  );
}

function BalanceTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  const auto = Number(p.auto_pnl || 0);
  const adopted = Number(p.adopted_pnl || 0);
  return (
    <div style={{ background: "#18181b", border: "1px solid #3f3f46", padding: "8px 10px", borderRadius: 6, fontSize: 12 }}>
      <div className="text-zinc-300 mb-1">{label}</div>
      <div className="text-blue-400">Balance: {fmtMoney(p.balance)}</div>
      {auto !== 0 && <div className="text-emerald-400">TradoPro automated: {fmtMoney(auto, true)}</div>}
      {adopted !== 0 && <div className="text-purple-400">Takes Control (adopted): {fmtMoney(adopted, true)}</div>}
    </div>
  );
}

export default function Statistics() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState("");
  const [overview, setOverview] = useState(null);
  const [riskEdit, setRiskEdit] = useState(false);     // guard: advanced editor collapsed by default
  const [riskForm, setRiskForm] = useState(null);       // percent values being edited
  const [riskDefaults, setRiskDefaults] = useState(null);
  const [riskSaving, setRiskSaving] = useState(false);
  const [riskMsg, setRiskMsg] = useState("");

  // Portfolio overview across ALL active accounts (Tier 2 governor).
  const loadOverview = React.useCallback(async () => {
    try {
      const res = await api.get("/statistics/portfolio-overview");
      setOverview(res.data);
      return res.data;
    } catch (e) {
      setOverview(null);
      return null;
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const PORTFOLIO_RISK_FIELDS = [
    ["correlation_group_fraction", "Correlation exposure cap", "Max correlated worst-case per symbol, as % of portfolio budget."],
    ["aggregate_fraction", "Aggregate exposure cap", "Max total worst-case across all accounts, as % of portfolio budget."],
    ["drawdown_reduce_fraction", "Drawdown reduce threshold", "Portfolio drawdown (% of budget) at which exposure is halved."],
    ["drawdown_halt_fraction", "Drawdown halt threshold", "Portfolio drawdown (% of budget) at which new exposure stops."],
    ["kelly_fraction", "Kelly fraction multiplier", "Fraction of full Kelly used when sizing each account."],
  ];

  const beginEditRisk = () => {
    const cfg = overview?.risk_config || {};
    setRiskDefaults(overview?.portfolio_risk_defaults || overview?.risk_config || {});
    setRiskForm(Object.fromEntries(PORTFOLIO_RISK_FIELDS.map(([k]) => [k, Math.round((cfg[k] ?? 0) * 100)])));
    setRiskMsg("");
    setRiskEdit(true);
  };

  const saveRisk = async () => {
    setRiskSaving(true);
    setRiskMsg("");
    try {
      const payload = Object.fromEntries(
        PORTFOLIO_RISK_FIELDS.map(([k]) => [k, Number(riskForm[k]) / 100])
      );
      await api.patch("/strategies/auto-trade/portfolio-risk-settings", payload);
      await loadOverview();
      setRiskMsg("Saved.");
      setRiskEdit(false);
    } catch (e) {
      setRiskMsg(e?.response?.data?.detail || "Could not save — check the values (each 1–100%, reduce < halt).");
    } finally {
      setRiskSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/broker-connections");
        const conns = Array.isArray(res.data) ? res.data : [];
        const accs = [];
        for (const c of conns) {
          for (const a of (c.trading_accounts || [])) {
            if (a.is_active !== false) accs.push({ id: a.id, connection_id: c.id, name: a.account_name || a.name || "Account", type: a.account_type });
          }
        }
        setAccounts(accs);
        if (accs.length && !accountId) setAccountId(accs[0].id);
      } catch (e) {
        setError("Could not load accounts.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/statistics/account-performance", {
          params: { trading_account_id: accountId, start, end },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) { setError("Could not load performance for this account."); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, start, end]);

  // Portfolio recommendation (advisory) — refreshes with the account.
  useEffect(() => {
    if (!accountId) { setPortfolio(null); return; }
    let cancelled = false;
    (async () => {
      setApplyMsg("");
      try {
        const res = await api.get("/statistics/portfolio-recommendation", {
          params: { trading_account_id: accountId },
        });
        if (!cancelled) setPortfolio(res.data);
      } catch (e) {
        if (!cancelled) setPortfolio(null);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  const applyRecommendation = async () => {
    const acc = accounts.find((a) => a.id === accountId);
    const rec = portfolio?.recommendation;
    if (!acc || !acc.connection_id || !rec) return;
    setApplying(true);
    setApplyMsg("");
    try {
      // Merge into the current risk settings (the PUT replaces all fields).
      const cur = (await api.get(`/broker-connections/${acc.connection_id}/accounts/${acc.id}/risk-settings`)).data || {};
      const payload = {
        ...cur,
        enable_max_position_loss: true,
        max_position_loss_type: "currency",
        max_position_loss_value: rec.recommended_max_position_loss,
        daily_drawdown_limit: rec.recommended_daily_loss_limit,
        max_position_size: Math.max(1, rec.recommended_contracts || 1),
      };
      await api.put(`/broker-connections/${acc.connection_id}/accounts/${acc.id}/risk-settings`, payload);
      setApplyMsg("Applied to risk settings.");
    } catch (e) {
      setApplyMsg("Could not apply — check risk settings permissions.");
    } finally {
      setApplying(false);
    }
  };

  const summary = data?.summary || {};
  const balanceSeries = data?.balance_over_time || [];
  const durationData = data?.trades_by_duration || [];
  const totalPnl = data?.closed_total_pnl ?? 0;

  // Mark days that included an adopted ("TradoPro Takes Control") position so they
  // can be highlighted on the balance line.
  const chartData = useMemo(
    () => balanceSeries.map((p) => ({ ...p, adopted_marker: Number(p.adopted_pnl || 0) !== 0 ? p.balance : null })),
    [balanceSeries]
  );

  const yDomain = useMemo(() => {
    const vals = balanceSeries.map((p) => p.balance);
    if (data?.max_loss_limit_balance != null) vals.push(data.max_loss_limit_balance);
    if (data?.profit_target_balance != null) vals.push(data.profit_target_balance);
    if (!vals.length) return ["auto", "auto"];
    const min = Math.min(...vals), max = Math.max(...vals);
    const pad = (max - min) * 0.1 || 100;
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [balanceSeries, data]);

  return (
    <PlatformShell title="Statistics" titleIcon={BarChart3} currentKey="statistics">
      <div className="p-4 md:p-6 space-y-4">
        {/* Title + subtitle */}
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <div>
            <h1 className="text-xl font-bold text-zinc-100 leading-none">Statistics</h1>
            <p className="text-xs text-zinc-500 mt-0.5">tradopro · performance</p>
          </div>
        </div>

        {/* Portfolio Overview — Tier 2 governor across ALL active accounts */}
        {overview && overview.accounts_count > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Portfolio Overview · all accounts</span>
                {(() => {
                  const st = overview.portfolio?.governor_status;
                  const cls = st === "halted" ? "text-red-400 border-red-500" : st === "reduced" ? "text-amber-400 border-amber-500" : "text-emerald-400 border-emerald-500";
                  return <span className={`text-xs font-normal border rounded-full px-2 py-0.5 ${cls}`}>governor: {st}</span>;
                })()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">Portfolio budget</div>
                  <div className="font-mono text-base text-zinc-100">{fmtMoney(overview.portfolio?.portfolio_budget)}</div>
                </div>
                <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">Aggregate worst-case</div>
                  <div className="font-mono text-base text-amber-400">{fmtMoney(overview.portfolio?.aggregate_worst_case)}</div>
                </div>
                <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">Aggregate drawdown</div>
                  <div className="font-mono text-base text-red-400">{fmtMoney(overview.portfolio?.aggregate_drawdown)}</div>
                </div>
                <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-zinc-500">Accounts</div>
                  <div className="font-mono text-base text-zinc-100">{overview.accounts_count}</div>
                </div>
              </div>

              {/* Correlation blocks (per symbol) */}
              {overview.portfolio?.per_symbol_exposure && Object.keys(overview.portfolio.per_symbol_exposure).length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-zinc-400 mb-1">Correlation blocks (worst-case by symbol)</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(overview.portfolio.per_symbol_exposure).map(([sym, v]) => (
                      <span key={sym} className="text-xs bg-zinc-950/60 border border-zinc-800 rounded-md px-2 py-1 font-mono">
                        {sym}: {fmtMoney(v)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-account allocation table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b border-zinc-800">
                      <th className="py-1.5 pr-3">Account</th>
                      <th className="py-1.5 pr-3">Symbol</th>
                      <th className="py-1.5 pr-3 text-right">Base</th>
                      <th className="py-1.5 pr-3 text-right">Allocated</th>
                      <th className="py-1.5 pr-3 text-right">Worst-case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview.portfolio?.accounts || []).map((a) => (
                      <tr key={a.account_id} className="border-b border-zinc-800/50">
                        <td className="py-1.5 pr-3 text-zinc-300">{a.name}</td>
                        <td className="py-1.5 pr-3 text-zinc-400">{a.symbol}</td>
                        <td className="py-1.5 pr-3 text-right font-mono text-zinc-500">{a.base_contracts}</td>
                        <td className={`py-1.5 pr-3 text-right font-mono ${a.scaled ? "text-amber-400" : "text-blue-400"}`}>
                          {a.allocated_contracts}{a.scaled ? " ↓" : ""}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono text-zinc-400">{fmtMoney(a.worst_case)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(overview.portfolio?.warnings || []).map((w, i) => (
                <div key={i} className="text-xs text-amber-400 mt-2">⚠ {w}</div>
              ))}
              {(overview.portfolio?.rationale || []).length > 0 && (
                <ul className="text-[11px] text-zinc-500 list-disc list-inside mt-2 space-y-0.5">
                  {overview.portfolio.rationale.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}

              {/* Advanced portfolio risk controls (collapsed by default to avoid casual changes) */}
              <div className="mt-4 border-t border-zinc-800 pt-3">
                {!riskEdit ? (
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-500">
                      Advanced portfolio risk controls govern the caps above. Defaults are conservative.
                    </div>
                    <button
                      type="button"
                      onClick={beginEditRisk}
                      className="text-xs border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-md px-2.5 py-1"
                    >
                      Edit advanced controls
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">⚠ Advanced portfolio risk controls</span>
                      <span className="text-[11px] text-zinc-500">Changing these directly affects recommended sizing across every account.</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {PORTFOLIO_RISK_FIELDS.map(([key, label, help]) => (
                        <label key={key} className="block bg-zinc-950/50 border border-zinc-800 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-300">{label}</span>
                            <span className="text-[10px] text-zinc-600">default {Math.round((riskDefaults?.[key] ?? 0) * 100)}%</span>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <input
                              type="number" min={1} max={100} step={1}
                              value={riskForm?.[key] ?? ""}
                              onChange={(e) => setRiskForm((f) => ({ ...f, [key]: e.target.value }))}
                              className="w-20 bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-sm font-mono"
                            />
                            <span className="text-xs text-zinc-500">%</span>
                          </div>
                          <div className="text-[10px] text-zinc-600 mt-1">{help}</div>
                        </label>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button" disabled={riskSaving} onClick={saveRisk}
                        className="text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-md px-3 py-1.5"
                      >
                        {riskSaving ? "Saving…" : "Save controls"}
                      </button>
                      <button
                        type="button" onClick={() => setRiskEdit(false)}
                        className="text-xs border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-md px-3 py-1.5"
                      >
                        Cancel
                      </button>
                      {riskMsg && <span className="text-xs text-amber-400">{riskMsg}</span>}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Toolbar: account dropdown + range (left) · KPI cards (right) */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm"
              data-testid="statistics-account-select"
            >
              {accounts.length === 0 && <option value="">No accounts</option>}
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.type ? ` · ${a.type}` : ""}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5" />
              <span>→</span>
              <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-5 py-2 text-right">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">Account Balance</div>
              <div className="text-2xl font-bold text-zinc-100">{fmtMoney(data?.account?.balance ?? 0)}</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-5 py-2 text-right">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">Closed Total PnL</div>
              <div className={`text-2xl font-bold ${totalPnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtMoney(totalPnl, true)}</div>
            </div>
          </div>
        </div>

        {error && <div className="text-red-400 text-sm">{error}</div>}
        {loading && <div className="text-zinc-400 text-sm">Loading…</div>}

        {/* Balance over time (filled area) + Account Summary side panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
            <CardHeader><CardTitle className="text-base">Account Balance Over Time</CardTitle></CardHeader>
            <CardContent style={{ height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                  <YAxis domain={yDomain} stroke="#71717a" fontSize={11}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    label={{ value: "Balance", angle: -90, position: "insideLeft", fill: "#71717a", fontSize: 11 }} />
                  <Tooltip content={<BalanceTooltip />} />
                  <Legend />
                  {data?.profit_target_balance != null && (
                    <ReferenceLine y={data.profit_target_balance} stroke="#22c55e" strokeDasharray="6 4"
                      label={{ value: "Profit Target", fill: "#22c55e", fontSize: 11, position: "insideTopLeft" }} />
                  )}
                  {data?.max_loss_limit_balance != null && (
                    <ReferenceLine y={data.max_loss_limit_balance} stroke="#ef4444" strokeDasharray="6 4"
                      label={{ value: "Max Loss Limit", fill: "#ef4444", fontSize: 11, position: "insideBottomLeft" }} />
                  )}
                  <Area type="monotone" dataKey="balance" name="Balance (TradoPro automated)" stroke="#3b82f6" strokeWidth={2} fill="url(#balFill)" dot={false} />
                  <Scatter dataKey="adopted_marker" name="Takes Control day (adopted)" fill="#a855f7" shape="circle" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader><CardTitle className="text-base">Account Summary</CardTitle></CardHeader>
            <CardContent>
              <StatRow label="Total trades" value={summary.total_trades ?? 0} />
              <StatRow label="Winning / Losing" value={`${summary.winning ?? 0} / ${summary.losing ?? 0}`} />
              <StatRow label="Best day" value={fmtMoney(summary.best_day ?? 0, true)} valueClass="text-emerald-400" />
              <StatRow label="Worst day" value={fmtMoney(summary.worst_day ?? 0)} valueClass="text-red-400" />
              <StatRow label="Highest balance" value={fmtMoney(summary.highest_balance ?? 0)} />
              <StatRow label="Lowest balance" value={fmtMoney(summary.lowest_balance ?? 0)} />
              {data?.account?.broker_max_loss != null && (
                <StatRow label={`Broker max loss (${data.account.broker_max_loss_type || "—"})`}
                  value={fmtMoney(data.account.broker_max_loss)} valueClass="text-amber-400" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Portfolio Management — recommended sizing & risk from broker max loss + edge */}
        {portfolio && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span>Portfolio Management</span>
                <span className="text-xs font-normal text-zinc-500">
                  edge from last {portfolio.inputs?.edge_window_days}d · {portfolio.inputs?.total_trades} trades
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!portfolio.account?.broker_max_loss ? (
                <div className="text-sm text-amber-400">
                  Set the broker-authorized max loss on this account (Settings → Trading Accounts) to enable sizing.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Headline numbers */}
                  <div className="space-y-3">
                    <div className="bg-zinc-950/50 border border-zinc-800 rounded-lg px-4 py-3">
                      <div className="text-[11px] uppercase tracking-wide text-zinc-500">Recommended contracts</div>
                      <div className="text-3xl font-bold text-blue-400">{portfolio.recommendation?.recommended_contracts}</div>
                    </div>
                    <StatRow label="Risk / trade" value={fmtMoney(portfolio.recommendation?.risk_per_trade)} />
                    <StatRow label="Per-contract risk" value={`${fmtMoney(portfolio.recommendation?.per_contract_risk)} (${portfolio.recommendation?.stop_points_used}pt)`} />
                    <StatRow label="Worst-case loss" value={fmtMoney(portfolio.recommendation?.worst_case_loss)} valueClass="text-amber-400" />
                  </div>
                  {/* Suggested risk settings + apply */}
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-zinc-400">Suggested risk settings</div>
                    <StatRow label="Max position loss" value={fmtMoney(portfolio.recommendation?.recommended_max_position_loss)} />
                    <StatRow label="Daily loss limit" value={fmtMoney(portfolio.recommendation?.recommended_daily_loss_limit)} />
                    <StatRow label="Edge (Kelly)" value={portfolio.recommendation?.kelly_fraction_raw != null ? portfolio.recommendation.kelly_fraction_raw.toFixed(3) : "—"} valueClass={portfolio.recommendation?.edge_positive ? "text-emerald-400" : "text-red-400"} />
                    <button
                      onClick={applyRecommendation}
                      disabled={applying || (portfolio.recommendation?.recommended_contracts || 0) < 1}
                      data-testid="apply-portfolio-recommendation"
                      className="mt-1 w-full rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm py-2"
                    >
                      {applying ? "Applying…" : "Apply to risk settings"}
                    </button>
                    {applyMsg && <div className="text-xs text-zinc-400">{applyMsg}</div>}
                  </div>
                  {/* Rationale + warnings */}
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-zinc-400">How this was computed</div>
                    <ul className="text-xs text-zinc-400 list-disc list-inside space-y-1">
                      {(portfolio.recommendation?.rationale || []).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    {(portfolio.recommendation?.warnings || []).map((w, i) => (
                      <div key={i} className="text-xs text-amber-400">⚠ {w}</div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trader Performance Tracker */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader><CardTitle className="text-base text-center">Trader Performance Tracker</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Gauge label="Win Rate Percent" value={summary.win_rate ?? 0} max={100}
                sublabel={`${(summary.win_rate ?? 0).toFixed(1)}%`} color="#f59e0b"
                caption={`${summary.winning ?? 0} of ${summary.total_trades ?? 0} trades`} />
              <Gauge label="Avg Winning Trade" value={summary.avg_win ?? 0} max={Math.max(1200, (summary.avg_win ?? 0) * 1.2)}
                sublabel={fmtMoney(summary.avg_win ?? 0)} color="#22c55e"
                caption={`across ${summary.winning ?? 0} wins`} />
              <Gauge label="Avg Losing Trade" value={summary.avg_loss ?? 0} max={Math.max(850, (summary.avg_loss ?? 0) * 1.2)}
                sublabel={fmtMoney(summary.avg_loss ?? 0)} color="#ef4444"
                caption={`across ${summary.losing ?? 0} losses`} />
            </div>
          </CardContent>
        </Card>

        {/* Successful Trades by Duration (half width, centered) */}
        <Card className="bg-zinc-900 border-zinc-800 w-full lg:w-1/2 mx-auto">
          <CardHeader><CardTitle className="text-base text-center">Successful Trades by Duration</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={durationData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="bucket" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46" }} />
                <Legend />
                <Bar dataKey="successful" name="Successful" stackId="a" fill="#22c55e" maxBarSize={44} />
                <Bar dataKey="unsuccessful" name="Unsuccessful" stackId="a" fill="#52525b" maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </PlatformShell>
  );
}
